import Button from '@semcore/ui/button';
import Checkbox from '@semcore/ui/checkbox';
import Modal from '@semcore/ui/modal';
import Tag from '@semcore/ui/tag';
import { Text } from '@semcore/ui/typography';
import { ContactDuplicateGroup, ContactDuplicateId } from '@src/types/duplicateContacts';
import {
  getInitialSelectedDuplicateContactIds,
  isDuplicateContactsSelectionValid,
  shouldShowAlreadyOnListBadge,
} from '@src/utils/duplicateContacts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DuplicateContactsModalProps {
  duplicatedContacts: ContactDuplicateGroup[];
  addedContactIds?: ContactDuplicateId[];
  isVisible: boolean;
  onClose: () => void;
  onConfirm: (selectedContactIds: Set<ContactDuplicateId>) => void;
}

export const DuplicateContactsModal = ({
  duplicatedContacts,
  addedContactIds = [],
  isVisible,
  onClose,
  onConfirm,
}: DuplicateContactsModalProps) => {
  const { t } = useTranslation(['mediaDatabase']);
  const addedContactIdsSet = new Set(addedContactIds);
  const [selectedContactIds, setSelectedContactIds] = useState(() =>
    getInitialSelectedDuplicateContactIds(duplicatedContacts),
  );

  const isSingleDuplicateGroup = duplicatedContacts.length === 1;
  const isConfirmDisabled = !isDuplicateContactsSelectionValid(
    duplicatedContacts,
    selectedContactIds,
  );

  const handleContactSelectionChange = (contactId: ContactDuplicateId, isChecked: boolean) => {
    setSelectedContactIds((currentSelection) => {
      const nextSelection = new Set(currentSelection);

      if (isChecked) {
        nextSelection.add(contactId);
      } else {
        nextSelection.delete(contactId);
      }

      return nextSelection;
    });
  };

  return (
    <Modal
      data-test="duplicate-contacts-modal"
      visible={isVisible}
      wMax={520}
      onClose={onClose}>
      <Modal.Title>
        {isSingleDuplicateGroup
          ? t('mediaDatabase:duplicateContactsModal.titleSingle')
          : t('mediaDatabase:duplicateContactsModal.titleMultiple')}
      </Modal.Title>
      <Text className="mb-6 block">
        {isSingleDuplicateGroup
          ? t('mediaDatabase:duplicateContactsModal.descriptionSingle')
          : t('mediaDatabase:duplicateContactsModal.descriptionMultiple')}
      </Text>
      <div className="flex flex-col gap-6">
        {duplicatedContacts.map(({ email, contacts, smartGroupId }, index) => (
          <div
            key={`${email}-${smartGroupId ?? index}`}
            className="flex flex-col gap-3">
            <Text>
              {t('mediaDatabase:duplicateContactsModal.sharedEmailLabel')}{' '}
              <span className="underline">{email}</span>
            </Text>
            {contacts.map((contact) => (
              <Checkbox
                key={contact.id}
                checked={selectedContactIds.has(contact.id)}
                className="gap-x-3"
                data-test={`duplicate-contact-checkbox-${contact.id}`}
                size="l"
                onChange={(isChecked) => {
                  handleContactSelectionChange(contact.id, isChecked);
                }}>
                <Checkbox.Value />
                <div className="flex items-center gap-2">
                  <Checkbox.Text
                    className="ml-0"
                    size={200}>
                    [{contact.name}]
                  </Checkbox.Text>
                  {shouldShowAlreadyOnListBadge(contact, addedContactIdsSet) && (
                    <Tag
                      className="shrink-0"
                      color="green-500"
                      data-test={`duplicate-contact-already-on-list-badge-${contact.id}`}>
                      {t('mediaDatabase:duplicateContactsModal.alreadyOnList')}
                    </Tag>
                  )}
                </div>
              </Checkbox>
            ))}
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-2">
        <Button
          data-test="duplicate-contacts-confirm-button"
          disabled={isConfirmDisabled}
          size="l"
          theme="success"
          use="primary"
          onClick={() => {
            onConfirm(selectedContactIds);
          }}>
          {t('mediaDatabase:duplicateContactsModal.confirm')}
        </Button>
        <Button
          data-test="duplicate-contacts-cancel-button"
          size="l"
          onClick={onClose}>
          {t('mediaDatabase:duplicateContactsModal.cancel')}
        </Button>
      </div>
    </Modal>
  );
};
